import { useEffect, useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import {
  CancelIssuedSaleInvoiceDocument,
  LastUsedCreditNoteSequencesDocument,
  SaleInvoicesListTableDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { SerialSequenceSelector } from 'modules/finance/components/SerialSequences/Selector'
import { toBaseSerialSequence } from 'modules/finance/components/SerialSequences/Selector/mappers'
import { BaseSerialSequenceFragment } from 'modules/finance/components/SerialSequences/Selector/types'

interface CancelIssuedSaleInvoiceModalProps {
  isOpen: boolean
  onClose: () => void
  invoiceId: string
  legalEntityId: string
  refetchQuery?: string
  onSuccess?: () => void
}

const CancelIssuedSaleInvoiceModal = ({
  isOpen,
  onClose,
  invoiceId,
  legalEntityId,
  refetchQuery,
  onSuccess,
}: CancelIssuedSaleInvoiceModalProps) => {
  const [selectedSequence, setSelectedSequence] = useState<BaseSerialSequenceFragment | null>(null)
  const [sequenceError, setSequenceError] = useState<string | undefined>()

  const [fetchLastUsedCreditNoteSequences] = useLazyQuery(LastUsedCreditNoteSequencesDocument)

  useEffect(() => {
    if (isOpen && legalEntityId && !selectedSequence) {
      fetchLastUsedCreditNoteSequences({
        variables: {
          legalEntityId,
        },
      }).then(({ data }) => {
        const serialSequence = data?.finance?.creditNoteSequencesConnection?.nodes?.[0]
        if (serialSequence) {
          setSelectedSequence(toBaseSerialSequence(serialSequence))
        }
      })
    }
  }, [isOpen, legalEntityId, fetchLastUsedCreditNoteSequences, selectedSequence])

  const [cancelIssuedSaleInvoice, { loading: canceling }] = useMutation(
    CancelIssuedSaleInvoiceDocument,
    {
      extractorFn: (data) => data.financeMutations.cancelIssuedSaleFinanceFinancialDocument,
      refetchQueries: refetchQuery
        ? [
            refetchQuery,
            SaleInvoicesListTableDocument,
            'ProjectDetailsPageSaleInvoicesPageInsights',
          ]
        : [SaleInvoicesListTableDocument, 'ProjectDetailsPageSaleInvoicesPageInsights'],
    }
  )

  const onSubmit = async () => {
    if (!selectedSequence?.id) {
      setSequenceError(i18n.t('finance.sale_invoices.cancel.sequence_required'))
      return
    }

    const result = await cancelIssuedSaleInvoice({
      variables: {
        id: invoiceId,
        serialSequenceId: selectedSequence.id,
      },
    })

    const handled = handleResult(result, (parsedErrors) => {
      app.setFlash({
        type: 'wrong',
        message: parsedErrors.errors ?? i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.sale_invoices.cancel.invoice_canceled'),
        time: 3000,
      })
      track(trackingEvents.accounting.saleInvoices.cancel)
      if (onSuccess) onSuccess()
      onClose()
    }
  }

  const handleSequenceSelect = (sequence: BaseSerialSequenceFragment | null) => {
    setSelectedSequence(sequence)
    if (sequence) {
      setSequenceError(undefined)
    }
  }

  const handleClose = () => {
    setSelectedSequence(null)
    setSequenceError(undefined)
    onClose()
  }

  if (!isOpen) return null

  const footerContent = (
    <>
      <Button type="secondary" onClick={handleClose}>
        {i18n.t('finance.sale_invoices.cancel.button_cancel')}
      </Button>
      <Button submit disabled={canceling || !selectedSequence?.id}>
        {i18n.t('finance.sale_invoices.cancel.issue_credit_note')}
      </Button>
    </>
  )

  return (
    <Modal
      illustration="alert"
      title={i18n.t('finance.sale_invoices.cancel.title')}
      onClose={handleClose}
      onSubmit={onSubmit}
      footer={footerContent}
    >
      <Box flexDirection="column" gap="s16">
        <Text.Normal>{i18n.t('finance.sale_invoices.cancel.content')}</Text.Normal>
        <Box paddingTop="s16">
          <SerialSequenceSelector
            type="credit_note"
            label={i18n.t('finance.sale_invoices.cancel.select_sequence')}
            mandatory
            queryParams={{
              status: 'active',
              legalEntityId: legalEntityId,
            }}
            value={selectedSequence}
            onSelect={handleSequenceSelect}
            error={sequenceError}
            variant="gamma"
          />
        </Box>
      </Box>
    </Modal>
  )
}

export const useCancelIssuedSaleInvoiceModal = () => {
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    invoiceId?: string
    legalEntityId?: string
    refetchQuery?: string
    onSuccess?: () => void
  }>({
    isOpen: false,
  })

  const openModal = ({
    invoiceId,
    legalEntityId,
    refetchQuery,
    onSuccess,
  }: {
    invoiceId: string
    legalEntityId: string
    refetchQuery?: string
    onSuccess?: () => void
  }) => {
    setModalState({
      isOpen: true,
      invoiceId,
      legalEntityId,
      refetchQuery,
      onSuccess,
    })
  }

  const closeModal = () => {
    setModalState({ isOpen: false })
  }

  const ModalComponent =
    modalState.isOpen && modalState.invoiceId && modalState.legalEntityId ? (
      <CancelIssuedSaleInvoiceModal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        invoiceId={modalState.invoiceId}
        legalEntityId={modalState.legalEntityId}
        refetchQuery={modalState.refetchQuery}
        onSuccess={modalState.onSuccess}
      />
    ) : null

  return {
    openModal,
    closeModal,
    ModalComponent,
  }
}
